import React, {useState, useEffect, useRef} from 'react';
import {useNavigate, useParams, useLocation, Navigate} from 'react-router-dom';
import {Layout} from '../../components/layout';
import {
    CarEntryHeader,
    ConsigneeSection,
    OrderDataSection,
    CarDataSection,
    PricesSection,
    RemarksSection,
    RelatedCarsSection,
    CarDataSectionRef,
    DuplicateChassisModal
} from '../../components/cars';
import {carService, voyageService, portService, relatedCarService} from '../../services';
import {Voyage, Consignee, CarCreate, Port, AttachmentStatus, AdditionalFeesMap, RelatedCar, Car} from '../../types';
import {useAuth} from '../../hooks/useAuth';
import {Toast} from '../../components';

interface CarEntryNavState {
    from?: {
        pathname: string;
        state?: {
            selectedCarId?: string;
            carsLoadedCount?: number;
            sortColumn?: string | null;
            sortDirection?: 'asc' | 'desc';
        };
    };
}

// Default dimensions for category 'L' units. Override model-provided values
// when category is 'L'. CBM is auto-calculated from L*W*H.
const L_CATEGORY_DEFAULTS = {
    length: 6.2,
    width: 2.5,
    height: 3.8,
    weight: 7000,
    cbm: parseFloat((6.2 * 2.5 * 3.8).toFixed(3)),
};

interface CarFormData {
    chassisNumber: string;
    name: string;
    address: string;
    bookingNumber: string;
    year: number | null;
    category: string;
    freight: number;
    additionalFees: AdditionalFeesMap;
    text: string;
    prepaidOrCollect: 'P' | 'C';
    collect: number;
    remarks: string;
    consigneeId: string;
    portId: string;
    shipperId: string;
    makeId: string;
    modelId: string;
    manualMakeModel: string;
    voyageId: string;
    length: number;
    width: number;
    height: number;
    weight: number;
    cbm: number;
    dateOfInput: string;
    lastEdited?: string;
}

const CarEntryPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const {voyageId, carId} = useParams<{ voyageId: string; carId?: string }>();
    const {user} = useAuth();
    const locationState = (location.state as CarEntryNavState | null) ?? null;
    const backLink = locationState?.from;

    // State for declarative navigation (forces re-render and proper navigation)
    const [shouldExit, setShouldExit] = useState(false);
    const [exitTarget, setExitTarget] = useState<{ path: string, state: any } | null>(null);

    const doNavigateBack = (selectedCarIdOverride?: string) => {
        const targetPath = backLink?.pathname || `/voyage-details/${voyageId}`;
        const navState = {
            selectedCarId: selectedCarIdOverride || carId || backLink?.state?.selectedCarId,
            carsLoadedCount: backLink?.state?.carsLoadedCount,
            sortColumn: backLink?.state?.sortColumn,
            sortDirection: backLink?.state?.sortDirection
        };

        // Set state to trigger re-render and declarative navigation
        // This ensures React properly processes the navigation after async operations
        setExitTarget({path: targetPath, state: navState});
        setShouldExit(true);
    };

    const [voyage, setVoyage] = useState<Voyage | null>(null);
    const [loading, setLoading] = useState(true);
    const [isEditMode, setIsEditMode] = useState(false);
    const [activeTab, setActiveTab] = useState<'main' | 'related'>('main');
    const [keepFreight, setKeepFreight] = useState(true);
    const [keepCollect, setKeepCollect] = useState(true);
    const [savedFreight, setSavedFreight] = useState<number | null>(null);
    const [savedCollect, setSavedCollect] = useState<number | null>(null);
    const [lastCreatedReferenceNumber, setLastCreatedReferenceNumber] = useState<number>(0);
    const [currentReferenceNumber, setCurrentReferenceNumber] = useState<number>(0);
    const [ports, setPorts] = useState<Port[]>([]);
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
    const [toast, setToast] = useState<{
        message: string;
        type: 'success' | 'error' | 'info' | 'warning';
        duration?: number
    } | null>(null);
    const [initialMakeModel, setInitialMakeModel] = useState<string>('');
    const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
    const [duplicateCars, setDuplicateCars] = useState<Car[]>([]);
    const [pendingSave, setPendingSave] = useState<{ continueAdding: boolean } | null>(null);

    const isLoadingVoyageRef = useRef(false);
    const isLoadingPortsRef = useRef(false);
    const isLoadingCarRef = useRef(false);
    const carDataSectionRef = useRef<CarDataSectionRef>(null);

    const [formData, setFormData] = useState<CarFormData>({
        chassisNumber: '',
        name: '',
        address: '',
        bookingNumber: '',
        year: null,
        category: '',
        freight: 0,
        additionalFees: {},
        text: '',
        prepaidOrCollect: 'P',
        collect: 0,
        remarks: '',
        consigneeId: '',
        portId: '',
        shipperId: '',
        makeId: '',
        modelId: '',
        manualMakeModel: '',
        voyageId: voyageId || '',
        length: 0,
        width: 0,
        height: 0,
        weight: 0,
        cbm: 0,
        dateOfInput: new Date().toISOString(),
        lastEdited: undefined
    });
    const [loadedValue, setLoadedValue] = useState<boolean>(false);
    const [initialPortDisplay, setInitialPortDisplay] = useState<string>('');
    const [initialShipperDisplay, setInitialShipperDisplay] = useState<string>('');
    const [initialConsigneeDisplay, setInitialConsigneeDisplay] = useState<string>('');
    const [relatedCars, setRelatedCars] = useState<Array<{
        id?: string;
        relation: AttachmentStatus;
        chassisNumber: string;
        makeId: string;
        modelId: string;
        manualMakeModel?: string;
        modelDisplayValue?: string;
        existingCarId?: string;
        remarks?: string;
        additionalFees?: AdditionalFeesMap;
    }>>([]);
    const [originalRelatedCarIds, setOriginalRelatedCarIds] = useState<string[]>([]);

    useEffect(() => {
        if (voyageId) {
            loadVoyage();
        } else {
            setLoading(false);
        }
        loadPorts();

        // Load car data if carId is provided (edit mode)
        if (carId) {
            setIsEditMode(true);
            loadCar();
        } else {
            setIsEditMode(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [voyageId, carId]);

    useEffect(() => {
        // Apply saved values if "Keep" is checked and we have saved values from a previous save
        if (keepFreight && savedFreight !== null) {
            setFormData(prev => ({...prev, freight: savedFreight}));
        }
        if (keepCollect && savedCollect !== null) {
            setFormData(prev => ({...prev, collect: savedCollect}));
        }
    }, [keepFreight, keepCollect, savedFreight, savedCollect]);

    const loadVoyage = async () => {
        if (isLoadingVoyageRef.current) return;
        try {
            if (!voyageId) return;
            isLoadingVoyageRef.current = true;
            const voyageData = await voyageService.getById(voyageId);
            setVoyage(voyageData);
        } catch (error) {
            console.error('Error loading voyage:', error);
        } finally {
            isLoadingVoyageRef.current = false;
            setLoading(false);
        }
    };

    const loadPorts = async () => {
        if (isLoadingPortsRef.current) return;
        try {
            isLoadingPortsRef.current = true;
            const portsData = await portService.getAll({limit: 1000});
            setPorts(portsData);
        } catch (error) {
            console.error('Error loading ports:', error);
        } finally {
            isLoadingPortsRef.current = false;
        }
    };

    const loadCar = async () => {
        if (isLoadingCarRef.current) return;
        try {
            if (!carId) return;
            isLoadingCarRef.current = true;
            const carData = await carService.getById(carId);

            // Set the reference number for display
            setCurrentReferenceNumber(carData.reference_number);

            // Store loaded value
            setLoadedValue(carData.loaded || false);

            // Set initial make/model from pre-loaded car data
            const makeName = carData.make?.name || '';
            const modelName = carData.model?.name || '';
            if (makeName && modelName) {
                setInitialMakeModel(`${makeName} - ${modelName}`);
            }

            // Set initial port/shipper/consignee display values
            if (carData.port) {
                setInitialPortDisplay(`${carData.port.port} (${carData.port.code})`);
            }
            if (carData.shipper) {
                setInitialShipperDisplay(`${carData.shipper.code} - ${carData.shipper.shortname || carData.shipper.name}`);
            }
            if (carData.consignee) {
                setInitialConsigneeDisplay(carData.consignee.code);
            }

            // Populate form with car data
            setFormData({
                chassisNumber: carData.chassis_number || '',
                name: carData.name || '',
                address: carData.address || '',
                bookingNumber: carData.booking_reference || '',
                year: carData.year ?? null,
                category: carData.category || carData.model?.type || '',
                freight: carData.freight || 0,
                additionalFees: carData.additional_fees || {},
                text: carData.freight_text || '',
                prepaidOrCollect: carData.prepaid_or_collect || 'P',
                collect: carData.collect || 0,
                remarks: carData.remarks || '',
                consigneeId: String(carData.consignee_id || ''),
                portId: String(carData.port_id || ''),
                shipperId: String(carData.shipper_id || ''),
                makeId: String(carData.make_id || ''),
                modelId: String(carData.model_id || ''),
                manualMakeModel: carData.manual_make_model || '',
                voyageId: String(carData.voyage_id || voyageId || ''),
                length: carData.length ?? carData.model?.length ?? 0,
                width: carData.width ?? carData.model?.width ?? 0,
                height: carData.height ?? carData.model?.height ?? 0,
                weight: carData.weight ?? carData.model?.weight ?? 0,
                cbm: carData.cbm ?? carData.model?.cbm ?? 0,
                dateOfInput: carData.date_of_input || new Date().toISOString(),
                lastEdited: carData.last_edited
            });

            // Load related cars from the car's related_cars property
            if (carData.related_cars && carData.related_cars.length > 0) {
                console.log(`Found ${carData.related_cars.length} related cars for car ${carId}`);

                const loadedRelatedCars = carData.related_cars.map((rc: RelatedCar) => {
                    // Build display value from make and model objects
                    let modelDisplayValue = '';
                    if (rc.make && rc.model) {
                        modelDisplayValue = `${rc.make.name} - ${rc.model.name}`;
                    } else if (rc.make) {
                        modelDisplayValue = rc.make.name;
                    } else if (rc.model) {
                        modelDisplayValue = rc.model.name;
                    }

                    return {
                        id: rc.id,
                        relation: (rc.relationship_type as AttachmentStatus) || 'STC',
                        chassisNumber: rc.chassis_number,
                        makeId: rc.make_id || '',
                        modelId: rc.model_id || '',
                        manualMakeModel: rc.manual_make_model || '',
                        modelDisplayValue,
                        remarks: rc.remarks || '',
                        additionalFees: rc.additional_fees || {}
                    };
                });
                setRelatedCars(loadedRelatedCars);
                // Track original IDs so we can detect deletions
                setOriginalRelatedCarIds(carData.related_cars.map((rc: RelatedCar) => rc.id));
            } else {
                setRelatedCars([]);
                setOriginalRelatedCarIds([]);
            }
        } catch (error) {
            console.error('Error loading car:', error);
            setToast({message: 'Failed to load car data', type: 'error'});
        } finally {
            isLoadingCarRef.current = false;
        }
    };

    const handlePodCodeChange = (portId: string) => {
        // Find port by ID (consignee.port_id is the port's UUID)
        const port = ports.find(p => p.id === portId);
        if (port) {
            setFormData(prev => ({...prev, portId: port.id}));
            // Update the display value so OrderDataSection shows the selection
            setInitialPortDisplay(`${port.port} (${port.code})`);
        }
    };

    const handleShipperFromConsignee = (shipperId: string, shipperCode?: string, shipperName?: string) => {
        setFormData(prev => ({...prev, shipperId}));
        if (shipperCode) {
            setInitialShipperDisplay(shipperName ? `${shipperCode} - ${shipperName}` : shipperCode);
        }
    };

    const handleConsigneeSelect = (consignee: Consignee) => {
        setFormData(prev => ({
            ...prev,
            consigneeId: consignee.id,
            name: consignee.name,
            address: consignee.address || ''
        }));
        setInitialConsigneeDisplay(consignee.code);
    };

    const handleModelSelect = (modelId: string, autoFillData?: {
        category?: string;
        length: number;
        width: number;
        height: number;
        weight: number;
        cbm: number
    }) => {
        setFormData(prev => {
            if (!autoFillData) return {...prev, modelId};
            const isL = autoFillData.category === 'L';
            const dims = isL ? L_CATEGORY_DEFAULTS : {
                length: autoFillData.length,
                width: autoFillData.width,
                height: autoFillData.height,
                weight: autoFillData.weight,
                cbm: autoFillData.cbm,
            };
            return {
                ...prev,
                modelId,
                ...(autoFillData.category && {category: autoFillData.category}),
                ...dims,
            };
        });
    };

    const handleCategoryChange = (category: string) => {
        setFormData(prev => {
            if (category === 'L') {
                return {...prev, category, ...L_CATEGORY_DEFAULTS};
            }
            return {...prev, category};
        });
    };

    const handleDimensionChange = (field: 'length' | 'width' | 'height' | 'weight' | 'cbm', value: number) => {
        setFormData(prev => {
            const newData = {...prev, [field]: value};
            // Auto-calculate CBM when length, width, or height changes
            if (field === 'length' || field === 'width' || field === 'height') {
                const l = field === 'length' ? value : prev.length;
                const w = field === 'width' ? value : prev.width;
                const h = field === 'height' ? value : prev.height;
                if (l > 0 && w > 0 && h > 0) {
                    newData.cbm = parseFloat((l * w * h).toFixed(3));
                } else {
                    newData.cbm = 0;
                }
            }
            return newData;
        });
    };

    const validateForm = (): boolean => {
        if (!formData.chassisNumber.trim()) {
            setToast({message: 'Please enter a chassis number', type: 'warning'});
            return false;
        }
        if (!formData.name.trim()) {
            setToast({message: 'Please enter a name', type: 'warning'});
            return false;
        }
        if (!formData.portId) {
            setToast({message: 'Please select a port of discharge', type: 'warning'});
            return false;
        }
        if (!formData.shipperId) {
            setToast({message: 'Please select a shipper', type: 'warning'});
            return false;
        }
        if ((!formData.makeId || !formData.modelId) && !formData.manualMakeModel.trim()) {
            setToast({message: 'Please select a make and model, or enter a manual make/model', type: 'warning'});
            return false;
        }
        if (!voyageId) {
            setToast({message: 'No voyage selected', type: 'error'});
            return false;
        }
        return true;
    };

    const saveRelatedCars = async (mainCarId: string) => {
        // Get current related car IDs (only those that already exist in DB)
        const currentRelatedCarIds = relatedCars
            .filter(rc => rc.id)
            .map(rc => rc.id as string);

        // Delete related cars that were removed (in original but not in current)
        const idsToDelete = originalRelatedCarIds.filter(id => !currentRelatedCarIds.includes(id));
        for (const idToDelete of idsToDelete) {
            console.log(`Deleting removed related car: ${idToDelete}`);
            await relatedCarService.delete(idToDelete);
        }

        // Create/update remaining related cars
        for (const relatedCar of relatedCars) {
            // Validate related car entry
            if (!relatedCar.chassisNumber.trim()) {
                console.warn('Skipping related car: missing chassis number');
                continue;
            }

            if (relatedCar.id) {
                // Case 1: Update existing RelatedCar record
                await relatedCarService.update(relatedCar.id, {
                    chassis_number: relatedCar.chassisNumber,
                    relationship_type: relatedCar.relation,
                    make_id: relatedCar.makeId || null,
                    model_id: relatedCar.modelId || null,
                    manual_make_model: relatedCar.manualMakeModel || null,
                    remarks: relatedCar.remarks || null,
                    additional_fees: relatedCar.additionalFees && Object.keys(relatedCar.additionalFees).length > 0
                        ? relatedCar.additionalFees
                        : null
                });
            } else if (relatedCar.existingCarId) {
                // Case 2: Convert existing Car to RelatedCar
                // This will: create RelatedCar, transfer its related cars, delete original car
                await carService.convertToRelated(
                    relatedCar.existingCarId,
                    mainCarId,
                    relatedCar.relation,
                    relatedCar.remarks || undefined,
                    relatedCar.additionalFees && Object.keys(relatedCar.additionalFees).length > 0
                        ? relatedCar.additionalFees
                        : undefined
                );
            } else {
                // Case 3: Create new RelatedCar record (brand new entry)
                await relatedCarService.create({
                    chassis_number: relatedCar.chassisNumber,
                    relationship_type: relatedCar.relation,
                    parent_car_id: mainCarId,
                    make_id: relatedCar.makeId || undefined,
                    model_id: relatedCar.modelId || undefined,
                    manual_make_model: relatedCar.manualMakeModel || undefined,
                    remarks: relatedCar.remarks || undefined,
                    additional_fees: relatedCar.additionalFees && Object.keys(relatedCar.additionalFees).length > 0
                        ? relatedCar.additionalFees
                        : undefined
                });
            }
        }
    };

    const handleSave = async (continueAdding: boolean = false, skipDuplicateCheck: boolean = false) => {
        console.log('handleSave called with continueAdding:', continueAdding);
        if (!validateForm()) {
            console.log('Validation failed');
            return;
        }
        if (!user) {
            setToast({message: 'User not authenticated', type: 'error'});
            return;
        }

        // Check for duplicate chassis (only for new entries or when chassis changed in edit mode)
        if (!skipDuplicateCheck && formData.chassisNumber.trim()) {
            try {
                const existingCars = await carService.checkChassisExists(
                    formData.chassisNumber.trim(),
                    isEditMode ? carId : undefined
                );
                if (existingCars.length > 0) {
                    setDuplicateCars(existingCars);
                    setPendingSave({ continueAdding });
                    setDuplicateModalOpen(true);
                    return;
                }
            } catch (error) {
                console.error('Error checking chassis:', error);
                // Continue with save if check fails
            }
        }

        console.log('Starting save process...');
        try {
            if (isEditMode && carId) {
                // Update existing car
                const carUpdateData = {
                    chassis_number: formData.chassisNumber,
                    name: formData.name,
                    address: formData.address || null,
                    booking_reference: formData.bookingNumber,
                    year: formData.year,
                    freight: formData.freight,
                    additional_fees: Object.keys(formData.additionalFees).length > 0 ? formData.additionalFees : null,
                    freight_text: formData.text || null,
                    prepaid_or_collect: formData.prepaidOrCollect,
                    collect: formData.prepaidOrCollect === 'C' ? formData.collect : null,
                    date_of_input: formData.dateOfInput,
                    last_edited: new Date().toISOString(),
                    remarks: formData.remarks || null,
                    loaded: loadedValue,
                    consignee_id: formData.consigneeId || null,
                    consignee_name: formData.name || undefined,
                    port_id: formData.portId,
                    shipper_id: formData.shipperId || undefined,
                    make_id: formData.makeId || undefined,
                    model_id: formData.modelId || undefined,
                    manual_make_model: formData.manualMakeModel || undefined,
                    voyage_id: formData.voyageId,
                    length: formData.length || undefined,
                    width: formData.width || undefined,
                    height: formData.height || undefined,
                    weight: formData.weight || undefined,
                    cbm: formData.cbm || undefined,
                    category: formData.category || null
                };

                await carService.update(carId, carUpdateData);
                console.log('Car updated successfully');

                // Save related cars
                await saveRelatedCars(carId);

                // Store success message for display after navigation
                sessionStorage.setItem('carEntrySuccess', 'Entry Updated');

                // Navigate back with the car selected
                doNavigateBack(carId);
            } else {
                // Create new car
                const carData: CarCreate = {
                    chassis_number: formData.chassisNumber,
                    name: formData.name,
                    address: formData.address || undefined,
                    booking_reference: formData.bookingNumber,
                    year: formData.year,
                    freight: formData.freight,
                    additional_fees: Object.keys(formData.additionalFees).length > 0 ? formData.additionalFees : undefined,
                    freight_text: formData.text || undefined,
                    prepaid_or_collect: formData.prepaidOrCollect,
                    collect: formData.prepaidOrCollect === 'C' ? formData.collect : undefined,
                    date_of_input: formData.dateOfInput,
                    remarks: formData.remarks || undefined,
                    loaded: false,
                    consignee_id: formData.consigneeId || undefined,
                    consignee_name: formData.name || undefined,
                    port_id: formData.portId,
                    shipper_id: formData.shipperId || undefined,
                    make_id: formData.makeId || undefined,
                    model_id: formData.modelId || undefined,
                    manual_make_model: formData.manualMakeModel || undefined,
                    voyage_id: voyageId,
                    length: formData.length || undefined,
                    width: formData.width || undefined,
                    height: formData.height || undefined,
                    weight: formData.weight || undefined,
                    cbm: formData.cbm || undefined,
                    category: formData.category || undefined
                };

                const createdCar = await carService.create(carData);
                console.log('Car created successfully:', createdCar);

                // Update the reference number with the created car's reference_number
                setLastCreatedReferenceNumber(createdCar.reference_number);

                // Save related cars
                await saveRelatedCars(createdCar.id);

                // Save freight and collect values if "Keep" is checked
                if (keepFreight) {
                    setSavedFreight(formData.freight);
                }
                if (keepCollect) {
                    setSavedCollect(formData.collect);
                }

                if (continueAdding) {
                    console.log('Save & Continue: Resetting car data fields...');
                    setFormData(prev => ({
                        ...prev,
                        chassisNumber: '',
                        makeId: '',
                        modelId: '',
                        manualMakeModel: '',
                        year: null,
                        category: '',
                        length: 0,
                        width: 0,
                        height: 0,
                        weight: 0,
                        cbm: 0,
                        freight: prev.freight,
                        collect: prev.collect,
                        dateOfInput: new Date().toISOString(),
                        lastEdited: undefined
                    }));
                    // Clear related cars and initial make/model for next entry
                    setRelatedCars([]);
                    setInitialMakeModel('');

                    setToast({message: 'Entry Saved', type: 'success', duration: 1000});
                    console.log('Toast set for Save & Continue');

                    // Focus chassis input for seamless continuous entry
                    setTimeout(() => {
                        carDataSectionRef.current?.focusChassisInput();
                    }, 0);
                } else {
                    // Store success message for display after navigation
                    sessionStorage.setItem('carEntrySuccess', 'Entry Saved');

                    // Navigate back with the newly created car selected
                    doNavigateBack(createdCar.id);
                }
            }
        } catch (error: any) {
            console.error('Error saving car:', error);

            // Check if it's the new uniform error format
            const errorDetail = error?.response?.data?.detail;
            if (errorDetail && typeof errorDetail === 'object' && errorDetail.error === 'duplicate_value') {
                // New uniform format: { error: "duplicate_value", field: "field_name", message: "..." }
                const fieldName = errorDetail.field;
                const message = errorDetail.message || 'Duplicate value, please choose another one';

                // Map backend field names to frontend field names
                const normalizedField = fieldName.toLowerCase().replace(/_/g, '');
                const fieldMapping: Record<string, string> = {
                    'chassisnumber': 'chassisNumber',
                    'referencenumber': 'referenceNumber'
                };
                const mappedField = fieldMapping[normalizedField] || normalizedField;
                setFieldErrors({[mappedField]: message});
            } else {
                // Fallback for other errors - show generic error in field errors
                const errorMessage = typeof errorDetail === 'string'
                    ? errorDetail
                    : (error?.message || 'Failed to save car entry. Please try again.');
                setFieldErrors({_general: errorMessage});
            }
        }
    };

    const handleDuplicateKeepNew = () => {
        setDuplicateModalOpen(false);
        if (pendingSave) {
            handleSave(pendingSave.continueAdding, true);
        }
        setPendingSave(null);
        setDuplicateCars([]);
    };

    const handleDuplicateCancel = () => {
        setDuplicateModalOpen(false);
        setPendingSave(null);
        setDuplicateCars([]);
    };

    const handleChooseExisting = (car: Car) => {
        setDuplicateModalOpen(false);
        setPendingSave(null);
        setDuplicateCars([]);
        // Navigate to the existing car's edit page
        navigate(`/car-entry/${car.voyage_id}/${car.id}`);
    };

    const handleKeyPress = (e: KeyboardEvent) => {
        if (e.key === 'F11') {
            e.preventDefault();
            handleSave(true);
        } else if (e.key === 'F12') {
            e.preventDefault();
            handleSave(false);
        } else if (e.key === 'Escape') {
            doNavigateBack();
        } else if (e.key === 'Enter') {
            const target = e.target as HTMLElement;
            // Only handle Enter for input fields and select elements (not textareas - allow Enter for multiline)
            if ((target.tagName === 'INPUT' || target.tagName === 'SELECT') &&
                target.getAttribute('type') !== 'submit' &&
                !target.closest('[data-dropdown-open="true"]')) {
                e.preventDefault();

                // Find the page container
                const pageContainer = target.closest('[data-car-entry-page]') as HTMLElement;
                if (!pageContainer) return;

                // Get all focusable elements within the page
                const focusableElements = pageContainer.querySelectorAll(
                    'input:not([disabled]):not([type="hidden"]):not([type="submit"]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]):not([type="submit"]), [tabindex]:not([tabindex="-1"])'
                );

                const focusableArray = Array.from(focusableElements) as HTMLElement[];
                const currentIndex = focusableArray.indexOf(target);

                if (currentIndex !== -1 && currentIndex < focusableArray.length - 1) {
                    // Focus next element
                    focusableArray[currentIndex + 1].focus();
                } else if (currentIndex === focusableArray.length - 1) {
                    // If last element, wrap to first
                    focusableArray[0]?.focus();
                }
            }
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyPress);
        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    });

    if (loading) {
        return (
            <Layout hideNavFooter>
                <div className="flex items-center justify-center h-screen">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="mt-4 text-gray-600 dark:text-gray-400">Loading...</p>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!voyage && voyageId) {
        return (
            <Layout hideNavFooter>
                <div className="flex items-center justify-center h-screen">
                    <div className="text-center">
                        <p className="text-red-600">Voyage not found</p>
                        <button
                            onClick={() => navigate(`/voyage-details/${voyageId}`)}
                            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md"
                        >
                            Back to Voyages
                        </button>
                    </div>
                </div>
            </Layout>
        );
    }

    // Declarative navigation: If we're ready to exit, render Navigate component
    // This forces React Router to execute navigation during the render phase
    if (shouldExit && exitTarget) {
        return <Navigate to={exitTarget.path} state={exitTarget.state} replace={true}/>;
    }

    return (
        <Layout fullWidth hideNavFooter>
            <div data-car-entry-page className="max-w-[1800px] mx-auto px-3">
                {(voyageId || voyage) && (
                    <div className="flex items-center space-x-2 mb-2">
                        {voyageId && (
                            <button
                                onClick={() => doNavigateBack()}
                                className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-lg font-semibold text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
                                aria-label="Back"
                            >
                                &lt;
                            </button>
                        )}
                        {voyage && (
                            <div className="flex-1">
                                <CarEntryHeader
                                    voyageCode={voyage.voyage_code}
                                    vessel={voyage.vessel_description}
                                    pol={voyage.port_name || ''}
                                    departure={voyage.departure}
                                    referenceNumber={isEditMode ? currentReferenceNumber : lastCreatedReferenceNumber}
                                    dateOfInput={formData.dateOfInput}
                                    lastEdited={formData.lastEdited}
                                />
                            </div>
                        )}
                    </div>
                )}

                {/* Main content based on active tab */}
                {activeTab === 'main' && (
                    <div className="grid grid-cols-12 gap-3 mt-1">
                        {/* Left Column: Consignee + Order Data */}
                        <div className="col-span-6 space-y-3">
                            <ConsigneeSection
                                consigneeId={formData.consigneeId}
                                name={formData.name}
                                address={formData.address}
                                onConsigneeSelect={handleConsigneeSelect}
                                onNameChange={(name) => {
                                    setFormData(prev => ({...prev, name, consigneeId: ''}));
                                    setInitialConsigneeDisplay('');
                                }}
                                onAddressChange={(address) => setFormData(prev => ({...prev, address}))}
                                onPodCodeChange={handlePodCodeChange}
                                onShipperIdChange={handleShipperFromConsignee}
                                initialConsigneeDisplay={initialConsigneeDisplay}
                            />
                            <OrderDataSection
                                portId={formData.portId}
                                shipperId={formData.shipperId}
                                bookingNumber={formData.bookingNumber}
                                onPortChange={(portId, displayLabel) => {
                                    setFormData(prev => ({...prev, portId}));
                                    if (displayLabel) setInitialPortDisplay(displayLabel);
                                }}
                                onShipperChange={(shipperId, displayLabel) => {
                                    setFormData(prev => ({...prev, shipperId}));
                                    if (displayLabel) setInitialShipperDisplay(displayLabel);
                                }}
                                onBookingNumberChange={(bookingNumber) => setFormData(prev => ({
                                    ...prev,
                                    bookingNumber
                                }))}
                                initialPortDisplay={initialPortDisplay}
                                initialShipperDisplay={initialShipperDisplay}
                            />
                        </div>

                        {/* Right Column: Car Data + Prices */}
                        <div className="col-span-6 space-y-3">
                            <CarDataSection
                                ref={carDataSectionRef}
                                chassisNumber={formData.chassisNumber}
                                makeId={formData.makeId}
                                modelId={formData.modelId}
                                manualMakeModel={formData.manualMakeModel}
                                initialMakeModel={initialMakeModel}
                                year={formData.year}
                                category={formData.category}
                                length={formData.length}
                                width={formData.width}
                                height={formData.height}
                                weight={formData.weight}
                                cbm={formData.cbm}
                                onChassisNumberChange={(value) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        chassisNumber: value
                                    }));
                                    // Clear chassis number error when user types
                                    if (fieldErrors.chassisNumber) {
                                        setFieldErrors(prev => {
                                            const newErrors = {...prev};
                                            delete newErrors.chassisNumber;
                                            return newErrors;
                                        });
                                    }
                                }}
                                onMakeChange={(makeId, displayLabel) => {
                                    setFormData(prev => ({
                                        ...prev,
                                        makeId,
                                        manualMakeModel: '' // Clear manual when selecting from DB
                                    }));
                                    if (displayLabel) {
                                        setInitialMakeModel(displayLabel);
                                    }
                                }}
                                onModelChange={handleModelSelect}
                                onManualMakeModelChange={(manualMakeModel) => setFormData(prev => ({
                                    ...prev,
                                    manualMakeModel,
                                    makeId: '', // Clear DB selection when entering manual
                                    modelId: ''
                                }))}
                                onYearChange={(year: number | null) => setFormData(prev => ({...prev, year}))}
                                onCategoryChange={handleCategoryChange}
                                onDimensionsChange={handleDimensionChange}
                                fieldErrors={fieldErrors}
                            />
                            <PricesSection
                                freight={formData.freight}
                                additionalFees={formData.additionalFees}
                                text={formData.text}
                                prepaidOrCollect={formData.prepaidOrCollect}
                                collect={formData.collect}
                                keepFreight={keepFreight}
                                keepCollect={keepCollect}
                                onFreightChange={(freight) => setFormData(prev => ({...prev, freight}))}
                                onAdditionalFeesChange={(additionalFees) => setFormData(prev => ({
                                    ...prev,
                                    additionalFees
                                }))}
                                onTextChange={(text) => setFormData(prev => ({...prev, text}))}
                                onPrepaidOrCollectChange={(prepaidOrCollect) => setFormData(prev => ({
                                    ...prev,
                                    prepaidOrCollect
                                }))}
                                onCollectChange={(collect) => setFormData(prev => ({...prev, collect}))}
                                onKeepFreightChange={setKeepFreight}
                                onKeepCollectChange={setKeepCollect}
                            />
                        </div>

                        {/* Full-Width Remarks Section */}
                        <div className="col-span-12">
                            <RemarksSection
                                remarks={formData.remarks}
                                onRemarksChange={(remarks) => setFormData(prev => ({...prev, remarks}))}
                            />
                        </div>
                    </div>
                )}

                {activeTab === 'related' && (
                    <RelatedCarsSection
                        mainCarId={carId}
                        voyageId={voyageId || ''}
                        shipperId={formData.shipperId}
                        relatedCars={relatedCars}
                        onRelatedCarsChange={setRelatedCars}
                        fieldErrors={fieldErrors}
                    />
                )}

                {/* General Error Display */}
                {fieldErrors._general && (
                    <div
                        className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center">
                        <svg className="w-5 h-5 text-red-600 dark:text-red-400 mr-2 flex-shrink-0" fill="none"
                             stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        <span className="text-sm text-red-700 dark:text-red-300">{fieldErrors._general}</span>
                    </div>
                )}

                {/* Footer with tabs and action buttons */}
                <div
                    className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-2 mt-3">
                    <div className="flex items-center justify-between">
                        {/* Left: Tabs */}
                        <div className="flex space-x-1">
                            <button
                                type="button"
                                onClick={() => setActiveTab('main')}
                                className={`px-3 py-1 rounded-md font-medium transition-colors text-sm ${
                                    activeTab === 'main'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                            >
                                Main
                            </button>
                            <button
                                type="button"
                                onClick={() => setActiveTab('related')}
                                className={`px-3 py-1 rounded-md font-medium transition-colors text-sm ${
                                    activeTab === 'related'
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                                }`}
                            >
                                Related
                                {relatedCars.length > 0 && (
                                    <span className={`ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-semibold rounded-full ${
                                        activeTab === 'related'
                                            ? 'bg-white text-blue-600'
                                            : 'bg-blue-600 text-white'
                                    }`}>
                                        {relatedCars.length}
                                    </span>
                                )}
                            </button>
                        </div>

                        {/* Right: Action buttons */}
                        <div className="flex items-center space-x-2">
                            {!isEditMode && (
                                <button
                                    type="button"
                                    onClick={() => handleSave(true)}
                                    className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors flex items-center text-sm shadow-sm"
                                >
                                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                              d="M5 13l4 4L19 7"/>
                                    </svg>
                                    Save & Continue (F11)
                                </button>
                            )}

                            <button
                                type="button"
                                onClick={() => handleSave(false)}
                                className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors flex items-center text-sm shadow-sm"
                            >
                                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                          d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4"/>
                                </svg>
                                {isEditMode ? 'Update Car' : 'Save & Exit (F12)'}
                            </button>

                            <button
                                type="button"
                                onClick={() => doNavigateBack()}
                                className="px-3 py-1.5 bg-gray-500 hover:bg-gray-600 text-white rounded-md font-medium transition-colors text-sm shadow-sm"
                            >
                                Exit (ESC)
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Toast Notification */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    duration={toast.duration}
                    onClose={() => setToast(null)}
                />
            )}

            {/* Duplicate Chassis Modal */}
            <DuplicateChassisModal
                isOpen={duplicateModalOpen}
                onClose={handleDuplicateCancel}
                onKeepNew={handleDuplicateKeepNew}
                onCancel={handleDuplicateCancel}
                onChooseExisting={handleChooseExisting}
                chassisNumber={formData.chassisNumber}
                existingCars={duplicateCars}
            />
        </Layout>
    );
};

export default CarEntryPage;
